import React, { useState, useMemo } from "react";
import { Wallet, Search } from "lucide-react";
import useWallets from "../hooks/useWallets";
import { formatINR } from "../utils/formatINR";
import { DateTime, MessageRow, Money, Mono, PageHeader, PageShell, TableCard, Td, Th, Toolbar, rowClass, tableClass, tbodyClass, theadClass } from "../components/ui/DataPage";

const formatCurrency = (amount, currency = "INR") =>
  currency === "INR"
    ? formatINR(amount || 0)
    : new Intl.NumberFormat("en-IN", { style: "currency", currency }).format(amount || 0);

// the same date and time as before, shown on two lines
const formatDay = (date) =>
  date ? new Date(date).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "N/A";
const formatClock = (date) =>
  date ? new Date(date).toLocaleString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }) : "";


export default function WalletsPage() {
  const { wallets = [], isLoading, error, refetch } = useWallets();
  const [search, setSearch] = useState("");

  const filteredWallets = useMemo(() => {
    if (!search) return wallets;
    return wallets.filter((w) =>
      w.user_id.toLowerCase().includes(search.toLowerCase())
    );
  }, [wallets, search]);

  const totalBalance = useMemo(() => {
    return filteredWallets.reduce((sum, w) => sum + parseFloat(w.balance || 0), 0);
  }, [filteredWallets]);

  return (
    <PageShell>
      <PageHeader icon={Wallet} title="Wallets" description="View user wallet balances and recent updates" />

      {/* Search + Summary */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_auto]">
        <Toolbar className="flex items-end">
          <label className="flex w-full max-w-md flex-col gap-1.5">
            <span className="text-xs font-medium text-neutral-600">Search</span>
            <span className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
              <input
                type="text"
                placeholder="Search by User ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="input w-full pl-9"
              />
            </span>
          </label>
        </Toolbar>

        {/* Total Balance */}
        <div className="relative overflow-hidden rounded-xl border border-neutral-200 bg-white px-6 py-4 shadow-card lg:min-w-[320px]">
          <div className="absolute inset-y-0 left-0 w-1 bg-brand-600" />
          <p className="text-xs font-medium uppercase tracking-[0.06em] text-neutral-500">Total Balance</p>
          <p className="mt-1 text-[28px] font-bold leading-9 tracking-tight tabular-nums text-brand-950">
            {isLoading ? <span className="text-neutral-300">—</span> : formatCurrency(totalBalance)}
          </p>
          <p className="text-xs text-neutral-500">
            {search ? "Wallets matching the search" : "All wallets"} · {filteredWallets.length.toLocaleString("en-IN")}
          </p>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-13 font-medium text-red-700">
          Error: {error}
        </div>
      )}

      {/* Table */}
      {!error && (
        <TableCard>
          <table className={tableClass}>
            <thead className={theadClass}>
              <tr>
                <Th>Wallet ID</Th>
                <Th>User ID</Th>
                <Th align="right">Balance</Th>
                <Th>Last Updated</Th>
              </tr>
            </thead>
            <tbody className={tbodyClass}>
              {isLoading ? (
                <MessageRow colSpan={4}>
                  <span className="inline-flex items-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-neutral-200 border-t-brand-600" />
                    Loading wallets...
                  </span>
                </MessageRow>
              ) : filteredWallets.length === 0 ? (
                <MessageRow colSpan={4}>No wallets found.</MessageRow>
              ) : (
                filteredWallets.map((w) => (
                  <tr key={w.wallet_id} className={rowClass}>
                    <Td>
                      <Mono>{w.wallet_id}</Mono>
                    </Td>
                    <Td>
                      <Mono>{w.user_id}</Mono>
                    </Td>
                    <Td align="right">
                      <Money>{formatCurrency(w.balance, w.currency)}</Money>
                    </Td>
                    <Td>
                      <DateTime date={formatDay(w.modified_at)} time={formatClock(w.modified_at)} />
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </TableCard>
      )}
    </PageShell>
  );
}
